/**
 * ThemeProvider - appearance presets: light (cream), dark, white, or legacy system.
 * Applies `data-theme` + `.dark` class on <html> so CSS tokens and Tailwind
 * `dark:` variants stay in sync.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { normalizeThemePreset, type ResolvedTheme, type ThemePreset } from "@/lib/themes";

interface ThemeContextValue {
  /** User's selected preset (may be "system"). */
  theme: ThemePreset;
  /** Active token set on the page. */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreset) => void;
  /** Cycles light → dark → white (skips system). */
  cycleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

const STORAGE_KEY = "ehr_theme";

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(theme: ThemePreset): ResolvedTheme {
  if (theme === "system") return getSystemTheme();
  return theme;
}

function readStoredTheme(defaultTheme: ThemePreset): ThemePreset {
  const stored = localStorage.getItem(STORAGE_KEY);
  return normalizeThemePreset(stored) ?? defaultTheme;
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemePreset;
}

export function ThemeProvider({ children, defaultTheme = "light" }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemePreset>(() => readStoredTheme(defaultTheme));

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme));

  const setTheme = useCallback((newTheme: ThemePreset) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  }, []);

  const cycleTheme = useCallback(() => {
    const order: ThemePreset[] = ["light", "dark", "white"];
    const current = theme === "system" ? resolvedTheme : theme;
    const idx = order.indexOf(current as (typeof order)[number]);
    const next = order[(idx + 1) % order.length] ?? "light";
    setTheme(next);
  }, [theme, resolvedTheme, setTheme]);

  useEffect(() => {
    const next = resolveTheme(theme);
    setResolvedTheme(next);

    const root = document.documentElement;
    root.dataset.theme = next;
    root.classList.remove("light", "dark");
    root.classList.add(next === "dark" ? "dark" : "light");
    root.style.colorScheme = next === "dark" ? "dark" : "light";
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolvedTheme(resolveTheme("system"));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, cycleTheme }),
    [theme, resolvedTheme, setTheme, cycleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export type { ThemePreset, ResolvedTheme };
